/** Lifecycle state of a branch. */
export type BranchStatus = 'active' | 'merged' | 'deleted'

/**
 * A commit-backed branch: a named pointer at a head commit in the ontology
 * commit DAG. This is the proxy's domain model served directly over REST
 * (the ConnectRPC branch surface was removed with the v1 branching rework).
 */
export interface Branch {
  /** Unique branch identifier (UUID). */
  id: string
  /** Human-readable branch name (display metadata; not unique). */
  name: string
  /** Hash of the commit this branch currently points at. */
  head: string
  /** Hash of the commit this branch was forked from, when forked. */
  forkedFrom?: string
  /**
   * ID of the branch this one was forked from, derived server-side from the
   * commit DAG at read time. Absent on main, and on branches whose fork
   * lineage cannot be resolved.
   */
  parentId?: string
  /**
   * Who created the branch: `user` (a person), `system` (the bootstrap main
   * row), `ingest` (an ingest job's own work branch), or `agent` (a branch an
   * agent authored, human-approved). `user`/`agent` are user-facing working
   * branches; `ingest`/`system` collapse in the picker — see `isSystemOrigin`.
   */
  origin: 'user' | 'system' | 'ingest' | 'agent'
  /** True only for main: never deleted, advances only via merge. */
  protected: boolean
  /** Current lifecycle state of the branch. */
  status: BranchStatus
  /** ISO 8601 UTC timestamp of when the branch was created. */
  createdAt: string
  /** Actor ID of the user who created the branch. */
  createdBy?: string
  /** ISO 8601 UTC timestamp of when the branch was merged, if status is `merged`. */
  mergedAt?: string
  /** Actor ID of the user who merged the branch, if status is `merged`. */
  mergedBy?: string
  /** ISO 8601 UTC timestamp of when the branch was deleted, if status is `deleted`. */
  deletedAt?: string
  /** Actor ID of the user who deleted the branch, if status is `deleted`. */
  deletedBy?: string
}

/**
 * One offset page of GET /branches (newest-first). `totalSize` is the filtered
 * total (honoring `origin` / `include_deleted`) across all pages, not the page
 * length — it sizes a numbered pager.
 */
export interface BranchPage {
  branches: Branch[]
  /** Total number of branches matching the filter, across all pages. */
  totalSize: number
}

/** Request body for POST /branches. */
export interface CreateBranchInput {
  /** Human-readable name for the new branch. */
  name: string
  /**
   * UUID of the active branch to fork from. Omitted (or the literal "main")
   * forks from main. Creating a branch is an O(1) pointer insert off the
   * source branch's head commit — no graph data is copied.
   */
  sourceId?: string
  /** Stable across retries of the same create request. */
  idempotencyKey?: string
}

/** Arguments for PATCH /branches/{id} (rename). */
export interface RenameBranchInput {
  /** UUID of the branch to rename. */
  id: string
  /** New human-readable name. Registry metadata only — nothing else moves. */
  name: string
}

/** Arguments for POST /branches/{id}:restore. */
export interface RestoreBranchInput {
  /** UUID of the branch to restore. */
  id: string
  /**
   * Commit hash to rebuild the working database from. Must be on the
   * branch's own history (its first-parent line down to the fork point).
   * Omitted = the branch head, i.e. "discard uncommitted changes".
   */
  commit?: string
  /** Stable across retries of the same restore request. */
  idempotencyKey?: string
}

/**
 * An immutable commit in the ontology DAG: a point in graph history recording
 * the delta from its parent, with a full snapshot cached alongside. The delta
 * payload itself is never in list responses — the detail endpoint returns it
 * explicitly (see `CommitDetail`).
 */
export interface Commit {
  /** Commit hash (32-char lowercase hex id). */
  hash: string
  /** Parent commit hash (first parent; the branch's previous head). */
  parentHash: string
  /** Second parent, present only on merge commits. */
  mergeParentHash?: string
  /** Object-storage URI of the cached full snapshot at this commit. */
  snapshotUri?: string
  /** Size of the cached snapshot artifact in bytes. */
  byteSize?: number
  /** Commit message, when one was given. */
  message?: string
  /** Actor ID of the committer. */
  author?: string
  /** ISO 8601 UTC timestamp of when the commit was recorded. */
  createdAt: string
  /**
   * True while the commit's itemized delta is still being computed off the
   * request path — a snapshot-only commit the async pipeline recorded, awaiting
   * the background worker; the change set (detail endpoint) is empty until it
   * clears. Carried on the history-list and commit-detail responses (absent on
   * the commit a create/merge returns inline); delta-less-by-design commits
   * (the root) are never pending.
   */
  deltaPending?: boolean
  /** True when the stored delta is known to contain no changes. */
  deltaEmpty?: boolean
  /**
   * True when this commit is a strict ancestor of the branch's fork point.
   * Inherited commits are read-only in branch history. The fork point itself
   * is false because restore/revert validation includes that boundary.
   */
  inherited?: boolean
}

/** One page of GET /branches/{id}/commits (first-parent history, newest first). */
export interface CommitPage {
  commits: Commit[]
  /** Opaque continuation token; absent on the last page. */
  nextPageToken?: string
}

/**
 * GET /commits/{hash}: commit metadata plus its stored delta payload — the
 * type-tagged GraphDelta document (decode with `decodeDelta` at render time).
 */
export interface CommitDetail extends Commit {
  /** The raw type-tagged delta document; absent for snapshot-only commits. */
  delta?: unknown
  /** True when the payload exceeded the response cap and was not embedded. */
  deltaOmitted?: boolean
}

/** Arguments for POST /branches/{id}/commits. */
export interface CreateCommitInput {
  /** UUID of the branch to commit. */
  branchId: string
  /** Optional commit message. */
  message?: string
}

/** Arguments for PATCH /commits/{hash}. */
export interface UpdateCommitMessageInput {
  /** Immutable commit hash. */
  hash: string
  /** Replacement human-authored message. */
  message: string
}

/**
 * A node on the merge/compare wire. `properties` values arrive type-tagged
 * (the same fidelity contract the stored delta ships) — decode with
 * `decodeTaggedProps` at render time.
 */
export interface TaggedNode {
  id: string
  labels?: string[]
  properties?: Record<string, unknown>
}

/** A relationship on the merge/compare wire; see `TaggedNode`. */
export interface TaggedRel {
  id: string
  type: string
  startId: string
  endId: string
  properties?: Record<string, unknown>
}

/**
 * One conflicted node: the three versions (null = absent on that side).
 * `fields` is the server's authoritative per-field conflict set — the
 * property names a per-field resolution must cover, computed with the merge
 * engine's own equality. null = whole-entity only (a side removed the entity
 * or labels diverge); [] = per-field applies but no property differs; absent
 * = a payload persisted before the server emitted it (fall back to a
 * client-side structural comparison, never rendered-text equality).
 */
export interface NodeConflict {
  id: string
  base: TaggedNode | null
  ours: TaggedNode | null
  theirs: TaggedNode | null
  fields?: string[] | null
}

/** One conflicted relationship; see `NodeConflict`. */
export interface RelConflict {
  id: string
  base: TaggedRel | null
  ours: TaggedRel | null
  theirs: TaggedRel | null
  fields?: string[] | null
}

/** The manual-resolution payload of a conflicted merge. */
export interface MergeConflicts {
  nodes: NodeConflict[]
  rels: RelConflict[]
  postgres?: PostgresConflict[]
}

/** Public row identity and conflicted columns; snapshot values remain private. */
export interface PostgresConflict {
  id: string
  table: string
  key: Record<string, unknown>
  kind: string
  fields?: string[]
}

/**
 * A type-tagged scalar value on the merge wire — the `{"t","v"}` codec form a
 * content-level resolution supplies. Strings and booleans are JSON-native;
 * ints and floats are tagged (payload a decimal string) so a large int or a
 * float's fidelity survives where a bare JSON number would not.
 */
export type TaggedValue = string | boolean | { t: 'i' | 'f'; v: string }

/**
 * One conflicting property's resolution: a side ("ours"/"theirs"), or a
 * corrected content value belonging to neither side (a fixed number, a merged
 * string).
 */
export type FieldResolution = 'ours' | 'theirs' | { value: TaggedValue }

/**
 * A "split" resolution for an add/add node collision — both branches newly
 * added a node under the same id with divergent content. Keep one side at the
 * original id and re-home the other side as a brand-new node under `newId`.
 */
export interface SplitResolution {
  keep: 'ours' | 'theirs'
  newId: string
}

/**
 * One conflict's resolution: a whole-entity side, a per-field map resolving
 * each conflicting property (a side or a content value), or a split of an
 * add/add node into two. A bare side still means every field. "ours" = target,
 * "theirs" = source.
 */
export type EntityResolution =
  | 'ours'
  | 'theirs'
  | Record<string, FieldResolution>
  | { split: SplitResolution }

/** Per-logical-id resolutions, split by entity kind. */
export interface MergeResolutions {
  nodes?: Record<string, EntityResolution>
  rels?: Record<string, EntityResolution>
  postgres?: Record<string, 'ours' | 'theirs'>
}

/** Arguments for POST /branches/{targetId}:merge. */
export interface MergeBranchInput {
  /** The branch whose head advances. */
  targetId: string
  /** The branch being merged in (consumed on success). */
  sourceId: string
  message?: string
  resolutions?: MergeResolutions
  /**
   * A prepare stage to apply without recomputing the plan; omitted recomputes.
   * A stale stage (a head moved since prepare) is rejected with STALE_PREVIEW.
   */
  stageHash?: string
}

/**
 * The merge outcome union: conflicts are data, not errors — the state layer
 * absorbs the API's 409 MERGE_CONFLICTS into this shape so components only
 * ever branch on `status`.
 */
export type MergeOutcome =
  | { status: 'merged'; commit: Commit; sourceId: string }
  | { status: 'conflicts'; conflicts: MergeConflicts }

/** Arguments for POST /branches/{id}:revertCommit. */
export interface RevertCommitInput {
  /** The branch whose head advances. */
  branchId: string
  /** The existing commit to undo. */
  commit: string
  message?: string
  resolutions?: MergeResolutions
}

/** Revert outcome union; conflicts reuse the merge resolver payload. */
export type RevertOutcome =
  | { status: 'reverted'; branch: Branch; commit: Commit }
  | { status: 'conflicts'; conflicts: MergeConflicts }

/** Per-group sizes of a compare result (also present when truncated). */
export interface CompareCounts {
  nodesAdded: number
  nodesChanged: number
  nodesRemoved: number
  relsAdded: number
  relsChanged: number
  relsRemoved: number
}

export interface PostgresCompareRow {
  table: string
  key: Record<string, unknown>
  columns?: string[]
}

export interface PostgresCompareDiff {
  added: PostgresCompareRow[]
  changed: PostgresCompareRow[]
  removed: PostgresCompareRow[]
}

export interface PostgresDiffCounts {
  added: number
  changed: number
  removed: number
}

/**
 * GET /branches/{targetId}:compare?source= — what merging the source into
 * the target would bring in: the source's changes relative to the merge
 * base, the same basis the merge reconciles over. Conflicts with the
 * target's own changes surface at merge time, not here. `truncated` means
 * the change set exceeded the response cap and only counts are present.
 */
export interface CompareResult {
  nodes?: {
    added: TaggedNode[]
    removed: TaggedNode[]
    changed: { before: TaggedNode; after: TaggedNode }[]
  }
  relationships?: {
    added: TaggedRel[]
    removed: TaggedRel[]
    changed: { before: TaggedRel; after: TaggedRel }[]
  }
  counts: CompareCounts
  truncated?: boolean
  postgres?: PostgresCompareDiff
  postgresCounts?: PostgresDiffCounts
  postgresTruncated?: boolean
  postgresHistoryUnavailable?: boolean
}

/** Per-group sizes for the read-only merge preview. */
export interface MergePreviewCounts extends CompareCounts {
  conflicts: number
}

export interface PostgresMergePreview {
  added: PostgresCompareRow[]
  changed: PostgresCompareRow[]
  removed: PostgresCompareRow[]
  conflicts: PostgresConflict[]
  counts: { added: number; changed: number; removed: number; conflicts: number }
  truncated?: boolean
  changesTruncated?: boolean
  conflictsTruncated?: boolean
}

/**
 * GET /branches/{targetId}:mergePreview?source= — a non-destructive 3-way
 * check over the current live branch state. Conflicts reuse the merge resolver
 * payload; `fastForward` means the target has not moved since the fork.
 */
export interface MergePreviewResult {
  conflicts: MergeConflicts
  counts: MergePreviewCounts
  postgres?: PostgresMergePreview
  postgresHistoryUnavailable?: boolean
  /**
   * The source-vs-base changes the merge brings in — the same shape the
   * standalone compare endpoint returns. Carried on the preview so the review
   * page reads both the conflict check and the incoming changes from one
   * request. Subject to the same oversized-payload truncation as compare.
   */
  incoming: CompareResult
  mergeBase: string
  fastForward: boolean
}

/**
 * POST /branches/{targetId}:prepareMerge — the preview that stages. It computes
 * the plan against the live databases first and only commits both dirty sides
 * (pinning the inputs to immutable heads) and stages the merged delta — returning
 * a `stageHash` that mergeBranch applies without recomputing — when the merge is
 * conflict-free AND brings in real changes. With conflicts it returns them
 * (resolve and re-prepare); with `noChanges` the source brings nothing in and
 * nothing was committed or staged. Otherwise the same shape as the read-only
 * preview (conflicts + incoming + counts).
 */
export interface PrepareMergeResult {
  /** Present when conflict-free with changes; pass to mergeBranch to apply without recomputing. */
  stageHash?: string
  conflicts: MergeConflicts
  /** True when the source brings no changes into the target — a no-op merge; nothing committed or staged. */
  noChanges?: boolean
  counts: MergePreviewCounts
  incoming: CompareResult
  mergeBase: string
  fastForward: boolean
}

/** Lifecycle state of a durable merge request. */
export type MergeRequestStatus = 'open' | 'merged' | 'closed'

/** Background operation kind on a merge request (preview vs apply). */
export type MergeWorkKind = 'preview' | 'apply'

/** Background operation status on a merge request. */
export type MergeWorkStatus = 'queued' | 'running' | 'succeeded' | 'failed'

/** True while preview/apply work is in flight — inputs must stay frozen. */
export function isMergeWorkActive(workStatus?: MergeWorkStatus | string | null): boolean {
  return workStatus === 'queued' || workStatus === 'running'
}

/** Who chose one conflict's resolution. */
export interface MergeResolutionProvenance {
  by: 'human' | 'agent'
  /** The agent's one-line reason, when `by` is `agent`. */
  rationale?: string
}

/**
 * The persisted resolution payload of a merge request: the type-tagged
 * resolutions split by entity kind, plus a per-logical-id provenance sidecar
 * (human vs agent). The same shape the merge accepts, so it round-trips
 * unchanged into apply.
 */
export interface StoredResolutions {
  nodes?: Record<string, EntityResolution>
  rels?: Record<string, EntityResolution>
  postgres?: Record<string, 'ours' | 'theirs'>
  provenance?: Record<string, MergeResolutionProvenance>
}

/**
 * Background work + result fields shared by merge-request detail and list rows.
 * Present after the first enqueue; terminal outcomes are discovered by polling.
 */
export interface MergeRequestWorkFields {
  workKind?: MergeWorkKind
  workStatus?: MergeWorkStatus
  workErrorReason?: string
  workErrorMessage?: string
  /** Requested merge commit message (apply). */
  workMessage?: string
  workActor?: string
  workStartedAt?: string
  workFinishedAt?: string
  /** Merge commit hash when apply succeeded. */
  resultCommitHash?: string
  /** True when the last preview found nothing to bring in. */
  noChanges?: boolean
}

/**
 * A durable merge request: the persisted review session for merging a source
 * branch into a target. It pins the heads it was last prepared against (for
 * drift detection), the in-progress resolutions chosen so far, and a lifecycle
 * status — the first-class entity the "merges in progress" page lists and the
 * review page resumes. Preview and apply run as background work on this row.
 */
export interface MergeRequest extends MergeRequestWorkFields {
  id: string
  sourceId: string
  targetId: string
  sourceHead: string
  targetHead: string
  /** Pre-minted merge commit hash of the staged conflict-free delta; absent when conflicts. */
  mergeHash?: string
  /**
   * The materialized review state, stored at preview so opening the PR is a read:
   * the conflict set (base/ours/theirs) and the incoming source-vs-base diff.
   */
  conflicts: MergeConflicts
  incoming?: CompareResult
  resolutions: StoredResolutions
  status: MergeRequestStatus
  title?: string
  createdBy?: string
  createdAt: string
  updatedAt: string
}

/** GET /mergeRequests/{id}: the request plus whether a side has drifted. */
export interface MergeRequestDetail extends MergeRequest {
  /**
   * True when either side's live head no longer matches the head the session
   * was pinned against — the resolver should refresh the conflict set and
   * re-check the saved picks.
   */
  headDrift: boolean
}

/**
 * Accepted body of POST :mergeRequest / POST :merge (202) — a queued detail.
 * Alias kept for call-site clarity; same shape as GET detail.
 */
export type OpenMergeRequestResult = MergeRequestDetail

/** One "merges in progress" list row (branch names + a resolved count). */
export interface MergeRequestListItem extends MergeRequestWorkFields {
  id: string
  sourceId: string
  sourceName: string
  targetId: string
  targetName: string
  status: MergeRequestStatus
  /** How many conflicts carry a resolution so far. */
  resolved: number
  /** Total conflicts in the request. */
  total: number
  title?: string
  createdBy?: string
  createdAt: string
  updatedAt: string
}

/** Arguments for POST /branches/{targetId}:mergeRequest (open or resume). */
export interface OpenMergeRequestInput {
  targetId: string
  sourceId: string
  title?: string
}

/** Arguments for PATCH /mergeRequests/{id} (autosave the in-progress picks). */
export interface PatchMergeRequestInput {
  id: string
  resolutions: StoredResolutions
}

/** An immutable point-in-time record of a branch. */
export interface Snapshot {
  /** Unique snapshot identifier (UUID). */
  id: string
  /** ID of the branch this snapshot was taken from. */
  branchId: string
  /** Optional human-readable label, e.g. "before-p-and-id-ingestion". */
  label: string
  /** ISO 8601 UTC timestamp of when the snapshot was created. */
  createdAt: string
  /** Actor ID of the user who created the snapshot. */
  createdBy: string
  /** Node count recorded with the snapshot (currently always 0). */
  nodeCount: number
}

/** Request body for POST /snapshots. */
export interface CreateSnapshotInput {
  /** ID of the branch to snapshot. Must refer to an active branch. */
  branchId: string
  /** Optional human-readable label for the snapshot. */
  label?: string
}

/** Active branch and site derived from the current request context. */
export interface BranchContext {
  /** Name of the active branch, or the baseline name if no branch is set. */
  branch: string
  /** Site ID extracted from the request context. */
  siteId: string
  /** Gateway ID extracted from the request context. */
  gatewayId: string
}
